import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import {
  DemographicsFields,
  DemographicsValue,
  emptyDemographics,
} from "./DemographicsFields";

type StudentStatus = "FT" | "PT" | "NA" | "";

// Mirrors the head-of-household MemberProfiles government-ID type options.
const GOV_ID_TYPE_KEYS = [
  "driversLicense",
  "stateId",
  "passport",
  "militaryId",
  "itin",
  "foreignGovId",
  "other",
] as const;
type GovIdType = (typeof GOV_ID_TYPE_KEYS)[number] | "";

export interface CoApplicantIdentityForm {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  ssn: string;
  noSsn: boolean;
  govIdType: GovIdType;
  govId: string;
  studentStatus: StudentStatus;
  demographics: DemographicsValue;
}

interface Props {
  defaultValues?: Partial<CoApplicantIdentityForm>;
  onSubmit: (data: CoApplicantIdentityForm) => void | Promise<void>;
  isSaving?: boolean;
}

const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;

const isAdultDob = (dob: string): boolean => {
  if (!dobRegex.test(dob)) return false;
  const [mm, dd, yyyy] = dob.split("/").map(Number);
  const birth = new Date(yyyy, mm - 1, dd);
  if (
    isNaN(birth.getTime()) ||
    birth.getFullYear() !== yyyy ||
    birth.getMonth() !== mm - 1 ||
    birth.getDate() !== dd
  ) {
    return false;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 18;
};

function dobToDateInput(value: string | undefined): string {
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? "");
  if (!us) return "";
  const month = Number(us[1]);
  const day = Number(us[2]);
  const year = Number(us[3]);
  const date = new Date(year, month - 1, day);
  if (
    month < 1 ||
    month > 12 ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "";
  }
  return `${us[3]}-${us[1]}-${us[2]}`;
}

function dateInputToDob(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!iso) return "";
  return `${iso[2]}/${iso[3]}/${iso[1]}`;
}

function dobInputMax(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

const formatSsn = (value: string): string =>
  value.replace(/\D/g, "").slice(0, 9);

export const CoApplicantIdentity: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const demographicsRequired = t(
    "affordable.application.demographics.required"
  ) as string;
  // Mirrors the head-of-household MemberProfiles demographics schema: every
  // field required unless the applicant declined to furnish.
  const demographicsSchema = yup.object({
    declined: yup.boolean().default(false),
    sex: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["M", "F", "X"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    ethnicity: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["1", "2", "3"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    race: yup.array().when("declined", {
      is: false,
      then: (s) => s.min(1, demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    disabled: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["1", "2", "3"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const schema = yup.object({
    firstName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.firstNameRequired") as string
      ),
    lastName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.lastNameRequired") as string
      ),
    dateOfBirth: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.dobRequired") as string
      )
      .matches(
        dobRegex,
        t("affordable.application.memberProfiles.dobError") as string
      )
      .test(
        "is-adult",
        t("affordable.application.memberProfiles.mustBe18") as string,
        (val) => !val || isAdultDob(val)
      ),
    noSsn: yup.boolean().default(false),
    ssn: yup.string().when("noSsn", {
      is: false,
      then: (s) =>
        s
          .required(
            t("affordable.application.memberProfiles.ssnRequired") as string
          )
          .matches(
            /^\d{9}$/,
            t("affordable.application.memberProfiles.ssnDigits") as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    govIdType: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdTypeRequired") as string
      ),
    govId: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdRequired") as string
      ),
    studentStatus: yup
      .string()
      .oneOf(["FT", "PT", "NA"])
      .required(
        t(
          "affordable.application.memberProfiles.studentStatusRequired"
        ) as string
      ),
    demographics: demographicsSchema,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CoApplicantIdentityForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      firstName: defaultValues?.firstName || "",
      lastName: defaultValues?.lastName || "",
      dateOfBirth: defaultValues?.dateOfBirth || "",
      ssn: defaultValues?.ssn || "",
      noSsn: defaultValues?.noSsn || false,
      govIdType: defaultValues?.govIdType || "",
      govId: defaultValues?.govId || "",
      studentStatus: defaultValues?.studentStatus || "",
      demographics: defaultValues?.demographics || emptyDemographics,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.coApplicant.identity.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.coApplicant.identity.subtitle")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t("formInput.firstName")} *`}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t("formInput.lastName")} *`}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  fullWidth
                  value={dobToDateInput(field.value)}
                  onChange={(e) =>
                    field.onChange(dateInputToDob(e.target.value))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: dobInputMax() }}
                  label={`${t("account.leaseApplication.birthDate")} *`}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="ssn"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={
                    t(
                      "affordable.application.memberProfiles.ssnLabel"
                    ) as string
                  }
                  type="password"
                  inputProps={{ inputMode: "numeric", maxLength: 9 }}
                  error={!!errors.ssn}
                  helperText={errors.ssn?.message}
                  onChange={(e) => field.onChange(formatSsn(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="noSsn"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("account.leaseApplication.noSsn") as string}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {t("affordable.application.memberProfiles.idSectionLabel")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="govIdType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.govIdType}>
                  <InputLabel>
                    {`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  </InputLabel>
                  <Select
                    {...field}
                    label={`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {GOV_ID_TYPE_KEYS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(
                          `affordable.application.memberProfiles.govIdTypes.${key}`
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.govIdType && (
                    <FormHelperText>
                      {errors.govIdType.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller
              name="govId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.memberProfiles.govIdLabel"
                  )} *`}
                  inputProps={{ autoComplete: "off" }}
                  error={!!errors.govId}
                  helperText={errors.govId?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {`${t(
                "affordable.application.memberProfiles.studentStatusLabel"
              )} *`}
            </Typography>
            <Controller
              name="studentStatus"
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.studentStatus}>
                  <RadioGroup row {...field} sx={{ gap: 2, flexWrap: "wrap" }}>
                    <FormControlLabel
                      value="FT"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusFullTime"
                      )}
                      sx={{ mr: 0 }}
                    />
                    <FormControlLabel
                      value="PT"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusPartTime"
                      )}
                      sx={{ mr: 0 }}
                    />
                    <FormControlLabel
                      value="NA"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusNotStudent"
                      )}
                      sx={{ mr: 0 }}
                    />
                  </RadioGroup>
                  {errors.studentStatus && (
                    <FormHelperText>
                      {errors.studentStatus.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <DemographicsFields control={control} basePath="demographics" />
          </Grid>
        </Grid>
      </RegisterReviewSection>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
